// Talent market (M3) — docs/ARCHITECTURE_M3.md §1, design plan §9.
// Era labor heat, company morale, the comp-policy dial, named stars, poach
// raids, and bust-era team lift-outs. Poach/star/lift-out decisions arrive as
// system-generated asks (one queue, one command — see stakeholders.ts).

import type { AskEffects, Discipline, StarEngineer } from "../types.js";
import { DISCIPLINES, stageAtLeast } from "../types.js";
import type { TickContext } from "../ctx.js";
import { emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { salaryOf } from "../commands.js";
import { clamp } from "../prng.js";
import { openSystemAsk } from "./stakeholders.js";

export function laborHeat(ctx: TickContext): number {
  return interpolate(ctx.content.economy.talent.heat, ctx.s.monthIndex);
}

/** Morale delta with clamping (every consumer goes through here). */
export function moraleHit(ctx: TickContext, delta: number): void {
  const t = ctx.s.talent;
  t.morale = clamp(t.morale + delta, 0, 100);
}

/** Offer fill rate (commands.ts hire): below-market comp doesn't close candidates
 *  in a hot market — the 1999 knife fight is fought with offer letters. */
export function hireFillRate(ctx: TickContext): number {
  const td = ctx.content.economy.talent;
  const comp = Math.min(0, ctx.s.talent.compPolicy); // paying up can't overfill a req
  return clamp(1 + (td.hireFillCompCoef + td.hireFillHeatCoef * laborHeat(ctx)) * comp, td.hireFillFloor, 1);
}

/** Attrition multiplier from morale and heat (people.ts). */
export function attritionMult(ctx: TickContext): number {
  const td = ctx.content.economy.talent;
  const t = ctx.s.talent;
  const morale = clamp(1 + (td.moraleBaseline - t.morale) * td.moraleAttritionCoef, td.moraleAttritionLo, td.moraleAttritionHi);
  const heat = 1 + td.heatAttritionCoef * laborHeat(ctx);
  const integration = t.integrationMonthsLeft > 0 ? td.integrationAttritionMult : 1;
  return morale * heat * integration;
}

/** Productivity multiplier from morale (people.ts EM budget). */
export function moraleProdMult(ctx: TickContext): number {
  const td = ctx.content.economy.talent;
  return clamp(1 + (ctx.s.talent.morale - td.moraleBaseline) * td.moraleProdCoef, td.moraleProdLo, td.moraleProdHi);
}

/** Effective star heads for a discipline (people.ts EM budget). */
export function starHeads(ctx: TickContext, d: Discipline): number {
  return ctx.s.talent.stars.reduce((a, star) => a + (star.discipline === d ? star.effHeads : 0), 0);
}

export function addStar(
  ctx: TickContext,
  spec: { name: string; discipline: Discipline; effHeads: number; salaryMult: number },
): StarEngineer {
  const star: StarEngineer = {
    id: `star-${ctx.s.counters.nextStar}`,
    name: spec.name,
    discipline: spec.discipline,
    effHeads: spec.effHeads,
    salaryMult: spec.salaryMult,
    joinedMonth: ctx.s.monthIndex,
  };
  ctx.s.counters.nextStar += 1;
  ctx.s.talent.stars.push(star);
  emit(ctx, "starJoined", `${star.name} joins — a ${star.discipline} name the whole industry knows.`, {
    starId: star.id,
    discipline: star.discipline,
    effHeads: star.effHeads,
  });
  return star;
}

export function removeStar(ctx: TickContext, starId: string, reason: string): void {
  const t = ctx.s.talent;
  const star = t.stars.find((x) => x.id === starId);
  if (!star) return;
  t.stars = t.stars.filter((x) => x.id !== starId);
  emit(ctx, "starLeft", `${star.name} ${reason}`, { starId, discipline: star.discipline });
}

function poolHeads(ctx: TickContext, d: Discipline): number {
  const pool = ctx.s.people.pools[d];
  return pool.productive + pool.ramping.reduce((a, r) => a + r.count, 0);
}

function totalHeads(ctx: TickContext): number {
  return DISCIPLINES.reduce((a, d) => a + poolHeads(ctx, d), 0);
}

/** Close-phase talent processing: morale drift, poach raids, star offers,
 *  lift-outs, integration countdown. Runs inside closeTick after street/board
 *  so underwater-stock drift reads this month's price. */
export function processTalentClose(ctx: TickContext): void {
  const { s, content } = ctx;
  if (s.dead) return;
  const td = content.economy.talent;
  const t = s.talent;
  const m = s.monthIndex;
  const heat = laborHeat(ctx);

  // morale mean-reverts to a baseline the comp policy shifts
  const baseline = clamp(td.moraleBaseline + td.compMoraleSpan * t.compPolicy, 0, 100);
  t.morale += (baseline - t.morale) * td.moraleReversionMonthly * ctx.monthsPerTick;

  // underwater paper (the light M3 version of the §9 attrition spiral; repricing is M4)
  if (s.street && s.ipo.ipoPrice !== null && s.street.sharePrice < td.underwaterPriceFrac * s.ipo.ipoPrice) {
    t.morale += td.underwaterMoraleDrift * ctx.monthsPerTick;
  }
  t.morale = clamp(t.morale, 0, 100);

  // acquisition-integration window
  if (t.integrationMonthsLeft > 0) {
    t.integrationMonthsLeft = Math.max(0, t.integrationMonthsLeft - ctx.monthsPerTick);
    if (t.integrationMonthsLeft === 0) {
      emit(ctx, "integrationDone", "The acquired team is integrated — churn subsides.", {});
    }
  }

  const heads = totalHeads(ctx);
  const salaryMonthly = (d: Discipline) => salaryOf(ctx, d) / 12;

  // ---- star poach raids: rivals hunt names first ----
  for (const star of [...t.stars]) {
    if (!ctx.rng.bernoulli(td.starPoachHazard * (0.5 + heat) * ctx.monthsPerTick)) continue;
    const cost = Math.round(td.starCounterCostMonths * salaryMonthly(star.discipline) * star.salaryMult);
    openSystemAsk(ctx, {
      source: "talent",
      headline: `A rival is courting ${star.name}`,
      body:
        `${star.name} has an offer in hand — a bigger title and a package your comp bands can't match. ` +
        `A retention grant would cost {cash0}. Letting a name walk tells the whole team something.`,
      monthsLeft: 1,
      defaultOption: 1,
      options: [
        { label: "Counter — pay the retention grant", effects: { cash: -cost, cashCategory: "retention", morale: -td.poachCounterMoraleHit } },
        { label: "Wish them well", effects: { starLeave: star.id, morale: -td.poachLossMoraleHit } },
      ],
    });
    emit(ctx, "poachRaid", `Poach raid: a rival is courting ${star.name}.`, { starId: star.id });
  }

  // ---- pool poach raids: clusters of senior people. A demoralized org is a
  // poaching TARGET — below-market comp reads as an invitation (design §9) ----
  const moraleBait = 1 + Math.max(0, td.moraleBaseline - t.morale) * 0.03;
  if (
    heads >= 10 &&
    stageAtLeast(s.stage, "vc") &&
    ctx.rng.bernoulli(Math.min(0.6, td.poachHazardBase * (0.5 + heat) * moraleBait) * ctx.monthsPerTick)
  ) {
    // target the deepest pool (deterministic tie-break: discipline order)
    let target: Discipline = DISCIPLINES[0]!;
    for (const d of DISCIPLINES) if (poolHeads(ctx, d) > poolHeads(ctx, target)) target = d;
    const count = Math.max(1, Math.min(td.poachClusterMax, Math.round(td.poachClusterFrac * poolHeads(ctx, target))));
    const cost = Math.round(td.poachCounterCostMonths * salaryMonthly(target) * count);
    openSystemAsk(ctx, {
      source: "talent",
      headline: `Poach raid on your ${target} team`,
      body:
        `A funded rival made offers to ${count} of your senior ${target} engineers — as a group. ` +
        `Matching means retention money ({cash0}) and everyone else hearing about it.`,
      monthsLeft: 1,
      defaultOption: 1,
      options: [
        { label: "Counter the offers", effects: { cash: -cost, cashCategory: "retention", morale: -td.poachCounterMoraleHit } },
        { label: "Let them go", effects: { losePool: { discipline: target, count }, morale: -td.poachLossMoraleHit } },
      ],
    });
    emit(ctx, "poachRaid", `Poach raid: ${count} ${target} engineers have rival offers.`, {
      discipline: target,
      count,
    });
  }

  // ---- star offers: names come on the market in hot eras (and to funded companies) ----
  if (stageAtLeast(s.stage, "vc") && t.stars.length < td.starMax) {
    const analog = s.path === "analog";
    const hazard = td.starOfferHazard * (0.5 + heat) * (analog ? td.starAnalogRarity : 1);
    if (ctx.rng.bernoulli(hazard * ctx.monthsPerTick)) {
      const name = td.starNames[(s.counters.nextStar - 1) % td.starNames.length]!;
      // stars follow the company's center of gravity: designers first
      const roll = ctx.rng.uniform();
      const discipline: Discipline = roll < 0.5 ? "design" : roll < 0.75 ? "verification" : "implementation";
      const effHeads = analog && discipline === "design" ? td.starEffHeadsAnalog : td.starEffHeadsDigital;
      const signing = Math.round(interpolate(td.starSigningCost, m));
      openSystemAsk(ctx, {
        source: "talent",
        headline: `${name} is available`,
        body:
          `${name} — a ${discipline} name with a following — is quietly looking. ` +
          `Signing means a {cash0} package and a salary far above band, but names like this bend schedules.`,
        monthsLeft: 2,
        defaultOption: 1,
        options: [
          {
            label: "Sign them",
            effects: { cash: -signing, cashCategory: "signingBonus", starJoin: { name, discipline, effHeads, salaryMult: td.starSalaryMult } },
          },
          { label: "Pass", effects: {} },
        ],
      });
      emit(ctx, "starOffer", `${name} (${discipline}) is available — signing costs real money.`, { name, discipline });
    }
  }

  // ---- bust-era lift-outs: whole teams from dead startups (design §9) ----
  const sentiment = interpolate(content.economy.sentiment, m);
  if (
    m >= td.liftOutFrom &&
    m <= td.liftOutTo &&
    sentiment < td.liftOutSentimentCeil &&
    stageAtLeast(s.stage, "vc") &&
    ctx.rng.bernoulli(td.liftOutHazard * ctx.monthsPerTick)
  ) {
    const size = td.liftOutSizeMin + ctx.rng.int(td.liftOutSizeMax - td.liftOutSizeMin + 1);
    const design = Math.max(1, Math.round(size * 0.5));
    const verif = Math.max(1, Math.round(size * 0.3));
    const impl = Math.max(0, size - design - verif);
    const cost = Math.round(td.liftOutCostPerHead * size);
    const hires: { discipline: Discipline; count: number }[] = [
      { discipline: "design", count: design },
      { discipline: "verification", count: verif },
    ];
    if (impl > 0) hires.push({ discipline: "implementation", count: impl });
    openSystemAsk(ctx, {
      source: "talent",
      headline: "A dead startup's team is available — whole",
      body:
        `Their investors pulled the plug; ${size} engineers who already work together want one offer for all of them. ` +
        `No recruiters, no ramp — they've shipped together before. Onboarding costs {cash0}.`,
      monthsLeft: 1,
      defaultOption: 1,
      options: [
        { label: `Hire all ${size}`, effects: { cash: -cost, cashCategory: "liftOut", hires, morale: 2 } },
        { label: "Pass", effects: {} },
      ],
    });
    emit(ctx, "liftOutOffer", `Team lift-out available: ${size} engineers from a dead startup.`, { size });
  }
}
